<template>
    <div
        v-cloak
        class="row"
    >
        <div class="col-xs-12">
            <div class="box box-primary">
                <div class="box-header with-border">
                    <h3 class="box-title">{{ $t('billing.table_title') }}</h3>

                    <div class="box-tools pull-right">
                        <button
                            type="button"
                            class="btn btn-sm btn-primary"
                            :disabled="loadingAccounts || loadingBalances"
                            @click="fetchAccounts(paginationOptions.current_page)"
                        >
                            <i
                                class="fa fa-refresh"
                                :class="{ 'fa-spin': loadingAccounts || loadingBalances }"
                                aria-hidden="true"
                            />
                            {{ $t('billing.refresh_all') }}
                        </button>
                    </div>
                </div>

                <div class="box-body with-border">
                    <p class="help-block no-margin">{{ $t('billing.sender_ip_help') }}</p>
                </div>

                <div class="box-body table-responsive no-padding">
                    <table class="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th>{{ $t('billing.provider') }}</th>
                                <th>{{ $t('billing.account') }}</th>
                                <th class="text-right">{{ $t('billing.balance') }}</th>
                                <th>{{ $t('billing.currency') }}</th>
                                <th>{{ $t('billing.checked_at') }}</th>
                                <th>{{ $t('billing.actions') }}</th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-if="loadingAccounts">
                                <td colspan="6">{{ $t('billing.loading_accounts') }}</td>
                            </tr>
                            <tr v-else-if="listFailed">
                                <td
                                    colspan="6"
                                    class="text-danger"
                                    role="alert"
                                >
                                    {{ $t('billing.accounts_unavailable') }}
                                </td>
                            </tr>
                            <tr v-else-if="!accounts.length">
                                <td colspan="6">{{ $t('billing.no_accounts') }}</td>
                            </tr>
                            <template v-else>
                                <tr
                                    v-for="account in accounts"
                                    :key="account.id"
                                >
                                    <td>{{ account.provider.name }}</td>
                                    <td>{{ account.name }}</td>
                                    <td
                                        v-if="account.loading"
                                        colspan="3"
                                    >
                                        {{ $t('billing.loading_balance') }}
                                    </td>
                                    <td
                                        v-else-if="account.failed"
                                        colspan="3"
                                        class="text-danger"
                                    >
                                        {{ balanceFailureMessage(account) }}
                                    </td>
                                    <template v-else>
                                        <td class="text-right">
                                            <div
                                                v-for="(balance, index) in account.balance.balances"
                                                :key="index"
                                                class="text-nowrap"
                                            >
                                                <span v-if="productLabel(balance)">{{ productLabel(balance) }}: </span>
                                                {{ balance.amount }}
                                            </div>
                                        </td>
                                        <td>
                                            <div
                                                v-for="(balance, index) in account.balance.balances"
                                                :key="index"
                                                class="text-nowrap"
                                            >
                                                {{ balance.currency || $t('billing.currency_not_supplied') }}
                                            </div>
                                        </td>
                                        <td>{{ account.balance.checked_at }}</td>
                                    </template>
                                    <td>
                                        <button
                                            type="button"
                                            class="btn btn-xs btn-default"
                                            :disabled="loadingAccounts || loadingBalances || account.loading"
                                            @click="refreshAccount(account)"
                                        >
                                            <i
                                                class="fa fa-refresh"
                                                :class="{ 'fa-spin': account.loading }"
                                                aria-hidden="true"
                                            />
                                            {{ $t('billing.refresh_account') }}
                                        </button>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>

                <div
                    v-if="paginationOptions.last_page > 1"
                    class="box-footer clearfix"
                >
                    <PaginationTable
                        :options="paginationOptions"
                        @change-page="fetchAccounts"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PaginationTable from '@/components/subcomponents/PaginationTable';

const MAX_BALANCE_REQUESTS = 3;
const ACTIONABLE_BALANCE_FAILURES = [
    'credentials_missing',
    'credentials_invalid',
    'credentials_incomplete',
    'authentication_failed',
    'sender_ip_unavailable',
    'sender_ip_conflict',
];

export default {
    components: {
        PaginationTable,
    },
    data() {
        return {
            accounts: [],
            paginationOptions: {
                disabled: true,
                current_page: 1,
                last_page: 1,
                total: 0,
            },
            loadingAccounts: false,
            loadingBalances: false,
            listFailed: false,
            requestGeneration: 0,
            balanceRequests: null,
            balanceCancelToken: null,
        };
    },
    mounted() {
        this.fetchAccounts();
    },
    beforeDestroy() {
        this.requestGeneration++;

        if (this.balanceCancelToken) {
            this.balanceCancelToken.cancel();
        }
    },
    methods: {
        balanceFailureMessage(account) {
            return this.$t(
                ACTIONABLE_BALANCE_FAILURES.includes(account.failureReason)
                    ? `billing.${account.failureReason}`
                    : 'billing.balance_unavailable'
            );
        },
        productLabel(balance) {
            if (balance.product_name) {
                return balance.product_name;
            }

            return Number.isInteger(balance.product_type) ? this.$t('billing.product', { type: balance.product_type }) : null;
        },
        async fetchAccounts(page = 1) {
            if (this.loadingAccounts) {
                return;
            }

            const generation = ++this.requestGeneration;
            const previousBalanceRequests = this.balanceRequests;

            if (this.balanceCancelToken) {
                this.balanceCancelToken.cancel();
            }

            this.loadingAccounts = true;
            this.loadingBalances = false;
            this.listFailed = false;
            this.paginationOptions.disabled = true;

            try {
                const {
                    data: {
                        accounts: { data: accounts, ...paginationOptions },
                    },
                } = await axios.get('/billing/data', { params: { page } });

                if (generation !== this.requestGeneration) {
                    return;
                }

                if (paginationOptions.current_page > paginationOptions.last_page) {
                    this.loadingAccounts = false;
                    await this.fetchAccounts(paginationOptions.last_page);
                    return;
                }

                this.accounts = accounts.map((account) => ({
                    ...account,
                    balance: null,
                    loading: true,
                    failed: false,
                    failureReason: null,
                }));
                this.paginationOptions = { ...paginationOptions, disabled: true };
            } catch (error) {
                if (generation === this.requestGeneration) {
                    this.accounts = [];
                    this.listFailed = true;
                }
            } finally {
                if (generation === this.requestGeneration) {
                    this.loadingAccounts = false;
                    this.paginationOptions.disabled = false;
                }
            }

            if (generation !== this.requestGeneration) {
                return;
            }

            if (this.listFailed || !this.accounts.length) {
                return;
            }

            this.loadingBalances = true;

            // Canceled requests must release their slots before the next page starts its balance requests.
            await previousBalanceRequests;

            if (generation !== this.requestGeneration) {
                return;
            }

            this.balanceCancelToken = axios.CancelToken.source();
            this.balanceRequests = this.fetchBalances(generation, this.balanceCancelToken.token);
            await this.balanceRequests;
        },
        async refreshAccount(account) {
            if (this.loadingAccounts || this.loadingBalances || account.loading || !this.accounts.includes(account)) {
                return;
            }

            account.loading = true;
            account.failed = false;
            account.failureReason = null;
            this.loadingBalances = true;
            this.balanceCancelToken = axios.CancelToken.source();
            this.balanceRequests = this.fetchBalances(this.requestGeneration, this.balanceCancelToken.token, [account]);
            await this.balanceRequests;
        },
        async fetchBalances(generation, cancelToken, accounts = this.accounts) {
            let nextIndex = 0;

            const fetchNext = async () => {
                while (nextIndex < accounts.length && generation === this.requestGeneration) {
                    const account = accounts[nextIndex++];
                    await this.fetchBalance(account, generation, cancelToken);
                }
            };

            await Promise.all(Array.from({ length: Math.min(MAX_BALANCE_REQUESTS, accounts.length) }, () => fetchNext()));

            if (generation === this.requestGeneration) {
                this.loadingBalances = false;
                this.balanceRequests = null;
                this.balanceCancelToken = null;
            }
        },
        async fetchBalance(account, generation, cancelToken) {
            try {
                const { data } = await axios.get(`/billing/accounts/${account.id}/balance`, { cancelToken });

                if (generation === this.requestGeneration) {
                    account.balance = data;
                }
            } catch (error) {
                if (generation === this.requestGeneration) {
                    account.failed = true;
                    const response = error && error.response;
                    account.failureReason = response && response.status === 422 && response.data ? response.data.error_code : null;
                }
            } finally {
                if (generation === this.requestGeneration) {
                    account.loading = false;
                }
            }
        },
    },
};
</script>
